// src/utils/netsuiteJournalEntry.js
import { calculateDdsDraw, isApprovalApproved } from "./associateFee";
import { NETSUITE_ACCOUNT_IDS } from "./contants";
import { torontoToday, torontoDateParts } from "./utils";

const toNum = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

const isMissing = (v) => v === null || v === undefined || v === "";
const hasId = (v) => !isMissing(v) && String(v).trim() !== "";

/**
 * Push a {account, debit|credit} line item.
 *  - amount > 0  -> use `positiveSide`  ("debit" or "credit")
 *  - amount < 0  -> use the other side, with absolute value
 *  - amount == 0 -> no line added
 */
function pushSignedLine(items, accountId, amount, positiveSide) {
  if (!accountId) return;
  const n = round2(amount);
  if (n === 0) return;
  const negativeSide = positiveSide === "debit" ? "credit" : "debit";
  const side = n > 0 ? positiveSide : negativeSide;
  items.push({
    account: { id: String(accountId) },
    [side]: Math.abs(n),
  });
}

/**
 * Validate a clinic's rows before creating a NetSuite journal entry.
 *
 * Rules:
 *  - Every row must have `production` and `collection` values (non-null).
 *  - Every row must have `netsuite_revenue_id` and `netsuite_af_expense_id`.
 *  - The three clinic-total account constants must be defined.
 *
 * @returns {{ ok: boolean, errors: string[] }}
 */
export function validateJournalEntryData({ subsidiaryId, clinicRows }) {
  const errors = [];

  if (!hasId(subsidiaryId)) {
    errors.push("Please set a valid NetSuite Subsidiary ID for this clinic.");
  }

  if (!Array.isArray(clinicRows) || clinicRows.length === 0) {
    errors.push(
      "No practitioner rows found for this clinic. Please add practitioners before creating a journal entry.",
    );
    return { ok: false, errors };
  }

  // Hard gate: every practitioner in the clinic must have admin approval
  // before we can post a journal entry. The UI also disables the button,
  // but we re-check here so the payload-builder can never produce a JE
  // for a clinic that still has pending rows.
  const unapproved = clinicRows.filter((r) => !isApprovalApproved(r.admin_approval));
  if (unapproved.length > 0) {
    const names = unapproved
      .map((r) => r.practitioner_name || `Practitioner #${r.practitioner_id ?? "?"}`)
      .join(", ");
    errors.push(
      `Cannot create journal entry: ${unapproved.length} practitioner${
        unapproved.length === 1 ? " is" : "s are"
      } not yet admin-approved (${names}).`,
    );
    return { ok: false, errors };
  }

  for (const r of clinicRows) {
    const who = r.practitioner_name || `Practitioner #${r.practitioner_id ?? "?"}`;
    // ?.total — MonthlyCompensationSerializer returns nested { total, breakdown }, not a plain number
    if (isMissing(r.production?.total)) {
      errors.push(`${who}: please enter a valid Production value.`);
    }
    if (isMissing(r.collection?.total)) {
      errors.push(`${who}: please enter a valid Collection value.`);
    }
    if (!hasId(r.netsuite_revenue_id)) {
      errors.push(`${who}: please set a valid NetSuite Revenue ID on the practitioner profile.`);
    }
    // AF Expense ID is required whenever there's something to post to it:
    // a non-zero DDS draw (from a fee % or tier ladder) or a non-zero
    // insurance amount.
    const draw = toNum(calculateDdsDraw(r));
    const insurance = toNum(r.insurance);
    const needsAfExpense = draw !== 0 || insurance !== 0;
    if (needsAfExpense && !hasId(r.netsuite_af_expense_id)) {
      const reason =
        draw !== 0 && insurance !== 0
          ? "they have a DDS draw and an insurance amount"
          : draw !== 0
            ? "they have a non-zero DDS draw"
            : "they have an insurance amount";
      errors.push(
        `${who}: please set a valid NetSuite AF Expense ID on the practitioner profile (required because ${reason}).`,
      );
    }
  }

  for (const [label, id] of [
    ["Accounts Receivable", NETSUITE_ACCOUNT_IDS.ACCOUNTS_RECEIVABLE],
    ["Associates Fee Payable", NETSUITE_ACCOUNT_IDS.ASSOCIATES_FEE_PAYABLE],
    ["Collection Account – Practices", NETSUITE_ACCOUNT_IDS.COLLECTION_ACCOUNT_PRACTICES],
  ]) {
    if (!hasId(id)) errors.push(`Please configure a valid NetSuite account ID for "${label}".`);
  }

  return { ok: errors.length === 0, errors };
}

/**
 * Build the `line.items` array for a NetSuite Journal Entry for a single clinic.
 *
 * Per-practitioner rows:
 *  - netsuite_revenue_id:    credit by (production - production_adjustment).
 *      Production adjustment reduces the revenue posting because it
 *      represents a write-down/correction against billed production.
 *  - netsuite_af_expense_id: debit  when dds_draw  > 0, credit when dds_draw  < 0
 *  - netsuite_af_expense_id: debit  by `insurance` (when insurance != 0). Posts the
 *    practitioner's health benefit cost into their AF expense account.
 *
 * Clinic totals:
 *  - ACCOUNTS_RECEIVABLE          = Σ ((production - production_adjustment)
 *                                       - (collection - collection_adjustment))
 *                                                                  debit if > 0, credit if < 0
 *  - ASSOCIATES_FEE_PAYABLE       = Σ dds_draw                    credit if > 0, debit if < 0
 *  - COLLECTION_ACCOUNT_PRACTICES = Σ (collection - collection_adjustment)
 *                                                                  debit if > 0, credit if < 0
 *  - HEALTH_BENEFITS_PRACTICES    = Σ insurance                   debit
 *  - HEALTH_BENEFIT_PAYABLE       = 2 * Σ insurance                credit (balances the
 *      per-practitioner AF debits + the 5499 clinic-total debit)
 */
export function buildJournalEntryItems(clinicRows) {
  const items = [];
  let totalNetProduction = 0;
  let totalNetCollection = 0;
  let totalDdsDraw = 0;
  let totalInsurance = 0;

  for (const r of clinicRows) {
    // ?.total — same nested format, used for JE line item calculations
    const production = toNum(r.production?.total);
    const collection = toNum(r.collection?.total);
    const productionAdj = toNum(r.production_adjustment?.total);
    const collectionAdj = toNum(r.collection_adjustment?.total);
    const netProduction = production - productionAdj;
    const netCollection = collection - collectionAdj;
    // Read the values straight off the row — no special-casing.
    // The backend already computed `breakdown.dds_draw` (fee % or tier).
    const draw = toNum(calculateDdsDraw(r));
    const insurance = toNum(r.insurance);

    totalNetProduction += netProduction;
    totalNetCollection += netCollection;
    totalDdsDraw += draw;
    totalInsurance += insurance;

    // Revenue: credit by (production - production_adjustment)
    pushSignedLine(items, r.netsuite_revenue_id, netProduction, "credit");
    // AF Expense: debit by dds_draw (positive) / credit (negative)
    pushSignedLine(items, r.netsuite_af_expense_id, draw, "debit");
    // AF Expense: debit by insurance (separate line so the health-benefit
    // posting stays attributable to the practitioner).
    pushSignedLine(items, r.netsuite_af_expense_id, insurance, "debit");
  }

  // AR = net production - net collection, debit if > 0
  pushSignedLine(
    items,
    NETSUITE_ACCOUNT_IDS.ACCOUNTS_RECEIVABLE,
    totalNetProduction - totalNetCollection,
    "debit",
  );
  // AF Payable = total dds draw, credit if > 0
  pushSignedLine(items, NETSUITE_ACCOUNT_IDS.ASSOCIATES_FEE_PAYABLE, totalDdsDraw, "credit");
  // Collection Account - Practices = net collection (after collection adjustment), debit if > 0
  pushSignedLine(
    items,
    NETSUITE_ACCOUNT_IDS.COLLECTION_ACCOUNT_PRACTICES,
    totalNetCollection,
    "debit",
  );

  // Health Benefits postings (when any practitioner has insurance):
  //  - per-practitioner debits on netsuite_af_expense_id (above) — Σ insurance
  //  - clinic-total debit  on HEALTH_BENEFITS_PRACTICES (5499)    — Σ insurance
  //  - clinic-total credit on HEALTH_BENEFIT_PAYABLE   (2591)    — 2 × Σ insurance
  // The 2× credit balances the two debit legs.
  if (round2(totalInsurance) !== 0) {
    pushSignedLine(items, NETSUITE_ACCOUNT_IDS.HEALTH_BENEFITS_PRACTICES, totalInsurance, "debit");
    pushSignedLine(
      items,
      NETSUITE_ACCOUNT_IDS.HEALTH_BENEFIT_PAYABLE,
      totalInsurance * 2,
      "credit",
    );
  }

  return items;
}

/**
 * Build the full NetSuite Journal Entry payload for a clinic.
 *
 * Throws an Error listing every missing id/value when validation fails.
 *
 * @param {Object} params
 * @param {string} params.memo            e.g. "Production, Collections and Associate Fee February 2026"
 * @param {string|number} params.subsidiaryId  clinic's NetSuite subsidiary internal id
 * @param {string} [params.trandate]      "YYYY-MM-DD" (defaults to today)
 * @param {Array}  params.clinicRows      MonthlyCompensation rows for the clinic
 */
export function buildJournalEntryPayload({ memo, subsidiaryId, trandate, clinicRows }) {
  const { ok, errors } = validateJournalEntryData({ subsidiaryId, clinicRows });
  if (!ok) {
    const err = new Error(
      `Please fix the following before creating the journal entry:\n- ${errors.join("\n- ")}`,
    );
    err.validationErrors = errors;
    throw err;
  }
  return {
    memo,
    subsidiary: { id: String(subsidiaryId) },
    trandate: trandate || torontoToday(),
    line: {
      items: buildJournalEntryItems(clinicRows),
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Intercompany Journal Entry (SFC pays Associate Fees on behalf of clinics)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * "YYYY-MM" -> "Month Year" (e.g. "2026-02" -> "February 2026"). Empty when
 * the input is missing/malformed.
 */
function formatMonthLong(month) {
  if (!month || typeof month !== "string") return "";
  const [y, m] = month.split("-").map((v) => parseInt(v, 10));
  if (!y || !m) return "";
  const d = new Date(y, m - 1, 1);
  return `${d.toLocaleString("en-US", { month: "long" })} ${y}`;
}

/**
 * Return "YYYY-MM-15" for the current (real-world) month.
 */
function defaultIntercompanyTrandate() {
  const { year, month } = torontoDateParts();
  return `${year}-${String(month).padStart(2, "0")}-15`;
}

/**
 * Build the per-clinic aggregated rows used by the intercompany journal
 * entry. Only includes clinics with a non-zero total DDS draw and a valid
 * NetSuite subsidiary id.
 *
 * @param {Array<[string, Array]>} clinicGroups  [ [clinic, rows[]], ... ]
 * @param {Object} subsidiaryByClinic            { clinic: netsuite_subsidiary_id }
 */
export function buildIntercompanyClinicTotals(clinicGroups, subsidiaryByClinic) {
  const out = [];
  for (const [clinic, clinicRows] of clinicGroups) {
    const sid = subsidiaryByClinic?.[clinic];
    if (sid === null || sid === undefined || sid === "") continue;
    let draw = 0;
    const practitioners = [];
    for (const r of clinicRows) {
      const d = toNum(calculateDdsDraw(r));
      if (d === 0) continue;
      draw += d;
      if (r.practitioner_name) practitioners.push(r.practitioner_name);
    }
    const rounded = round2(draw);
    if (rounded === 0) continue;
    out.push({
      clinic,
      subsidiaryId: sid,
      ddsDraw: rounded,
      practitioners,
    });
  }
  return out;
}

/**
 * Validate the inputs for an intercompany journal entry.
 */
export function validateIntercompanyJournalEntryData(clinicTotals) {
  const errors = [];
  if (!Array.isArray(clinicTotals) || clinicTotals.length === 0) {
    errors.push("No clinics with a non-zero DDS draw were found for this month.");
  }
  for (const [label, id] of [
    ["Smiles First Corporation (subsidiary)", NETSUITE_ACCOUNT_IDS.SMILES_FIRST_CORPORATION],
    ["Associates Fee Payable", NETSUITE_ACCOUNT_IDS.ASSOCIATES_FEE_PAYABLE],
    [
      "Loan: From Smiles First Corporation",
      NETSUITE_ACCOUNT_IDS.LOAN_FROM_SMILES_FIRST_CORPORATION,
    ],
  ]) {
    if (!hasId(id)) errors.push(`Please configure a valid NetSuite id for "${label}".`);
  }
  return { ok: errors.length === 0, errors };
}

/**
 * Build the NetSuite Intercompany Journal Entry payload.
 *
 * Layout (header subsidiary = SFC):
 *   1. SFC : ASSOCIATES_FEE_PAYABLE          credit  Σ dds_draw   memo
 *   2. SFC : LOAN_FROM_SMILES_FIRST_CORPORATION debit Σ dds_draw  memo
 *   for each clinic with non-zero dds_draw:
 *     3. clinic : LOAN_FROM_SMILES_FIRST_CORPORATION credit clinic_dds_draw  memo: ""
 *     4. clinic : ASSOCIATES_FEE_PAYABLE             debit  clinic_dds_draw  memo: <practitioner names>
 *
 * @param {Object} params
 * @param {string} params.month                 "YYYY-MM"
 * @param {Array}  params.clinicGroups          [ [clinic, rows[]], ... ]
 * @param {Object} params.subsidiaryByClinic    { clinic: netsuite_subsidiary_id }
 * @param {string} [params.trandate]            "YYYY-MM-DD" (default 15th of month)
 */
export function buildIntercompanyJournalEntryPayload({
  month,
  clinicGroups,
  subsidiaryByClinic,
  trandate,
}) {
  const clinicTotals = buildIntercompanyClinicTotals(clinicGroups, subsidiaryByClinic);
  const { ok, errors } = validateIntercompanyJournalEntryData(clinicTotals);
  if (!ok) {
    const err = new Error(
      `Please fix the following before creating the intercompany journal entry:\n- ${errors.join(
        "\n- ",
      )}`,
    );
    err.validationErrors = errors;
    throw err;
  }

  const monthLabel = formatMonthLong(month);
  const memo = `Associate Fee ${monthLabel} - paid by SFC - Smiles Analytics`;

  const totalDraw = round2(clinicTotals.reduce((s, c) => s + c.ddsDraw, 0));
  const sfcId = String(NETSUITE_ACCOUNT_IDS.SMILES_FIRST_CORPORATION);
  const afPayable = String(NETSUITE_ACCOUNT_IDS.ASSOCIATES_FEE_PAYABLE);
  const loanFromSfc = String(NETSUITE_ACCOUNT_IDS.LOAN_FROM_SMILES_FIRST_CORPORATION);

  const items = [];

  // SFC aggregate lines
  items.push({
    lineSubsidiary: { id: sfcId },
    account: { id: afPayable },
    credit: totalDraw,
    memo,
  });
  items.push({
    lineSubsidiary: { id: sfcId },
    account: { id: loanFromSfc },
    debit: totalDraw,
    memo,
  });

  // Per-clinic lines
  for (const c of clinicTotals) {
    const clinicSub = String(c.subsidiaryId);
    items.push({
      lineSubsidiary: { id: clinicSub },
      account: { id: loanFromSfc },
      credit: c.ddsDraw,
      memo: "",
    });
    items.push({
      lineSubsidiary: { id: clinicSub },
      account: { id: afPayable },
      debit: c.ddsDraw,
      memo: c.practitioners.join(", "),
    });
  }

  return {
    subsidiary: { id: sfcId },
    memo,
    trandate: trandate || defaultIntercompanyTrandate(),
    line: { items },
  };
}
